import gql from 'graphql-tag';

export const getShoppingListsQuery = gql`
    query getShoppingLists($options: ShoppingListListOptions) {
        getShoppingLists(options: $options) {
            items {
                id
                createdAt
                name
                token
                isPrivate
                enabled
                assignmentType
                createdByName
                startDate
                endDate
                createdBy {
                    id
                    identifier
                }
                channels {
                    id
                    code
                    customFields {
                        channelName
                        channelCode
                    }
                }
                image {
                    id
                    name
                    source
                    createdAt
                    updatedAt
                    type
                    fileSize
                    mimeType
                    width
                    height
                    preview
                    focalPoint {
                        x
                        y
                    }
                    tags {
                        id
                        value
                    }
                }
            }
            totalItems
        }
    }
`;

export const getShoppingListByIdQuery = gql`
    query getShoppingList($id: ID!) {
        getShoppingList(id: $id) {
            id
            createdAt
            name
            description
            token
            isPrivate
            enabled
            assignmentType
            createdByName
            startDate
            endDate
            createdBy {
                id
                identifier
            }
            channels {
                id
                code
                customFields {
                    channelName
                    channelCode
                }
            }
            segments {
                id
                code
                description
            }
            image {
                id
                name
                source
                createdAt
                updatedAt
                type
                fileSize
                mimeType
                width
                height
                preview
                focalPoint {
                    x
                    y
                }
                tags {
                    id
                    value
                }
            }
        }
    }
`;

export const customerSegmentsQuery = gql`
    query customerSegments($input: CustomerSegmentsInput) {
        customerSegments(input: $input) {
            items {
                id
                code
                description
            }
            totalItems
        }
    }
`;

export const getCustomersInShoppingListQuery = gql`
    query getCustomersInShoppingListQuery(
        $shoppingListId: ID!
        $skip: Int
        $take: Int
        $document: String
    ) {
        getCustomersInShoppingList(
            shoppingListId: $shoppingListId
            options: { skip: $skip, take: $take }
            document: $document
        ) {
            items {
                id
                title
                firstName
                lastName
                emailAddress
                phoneNumber
                customFields {
                    document
                }
            }
            totalItems
        }
    }
`;

export const getProductsInShoppingListQuery = gql`
    query getProductsInShoppingList(
        $shoppingListId: ID!
        $skip: Int
        $take: Int
        $promotionId: ID
    ) {
        getProductsInShoppingList(
            shoppingListId: $shoppingListId
            options: { skip: $skip, take: $take }
            promotionId: $promotionId
        ) {
            items {
                id
                quantity
                promotionIds
                isMandatoryPromotionProduct
                channel {
                    id
                    code
                    customFields {
                        channelName
                        channelCode
                    }
                }
                productVariant {
                    id
                    name
                    product {
                        id
                        name
                    }
                }
            }
            totalItems
        }
    }
`;

export const getShoppingListPromotionsQuery = gql`
    query getShoppingListPromotions($shoppingListId: ID!) {
        getShoppingListPromotions(shoppingListId: $shoppingListId) {
            id
            promotionId
            name
            startsAt
            endsAt
            productCount
            mandatoryProductCount
        }
    }
`;

export const getShoppingListPromotionSharingEligibleCustomersQuery = gql`
    query getShoppingListPromotionSharingEligibleCustomers(
        $shoppingListId: ID!
        $promotionId: ID!
        $document: String
        $skip: Int
        $take: Int
    ) {
        getShoppingListPromotionSharingEligibleCustomers(
            input: {
                shoppingListId: $shoppingListId
                promotionId: $promotionId
                document: $document
                skip: $skip
                take: $take
            }
        ) {
            items {
                id
                title
                firstName
                lastName
                emailAddress
                phoneNumber
                document
            }
            totalItems
        }
    }
`;

export const getShoppingListPromotionImportOptionsQuery = gql`
    query getShoppingListPromotionImportOptions(
        $shoppingListId: ID!
        $searchTerm: String
        $skip: Int
        $take: Int
    ) {
        getShoppingListPromotionImportOptions(
            shoppingListId: $shoppingListId
            input: { searchTerm: $searchTerm, skip: $skip, take: $take }
        ) {
            items {
                id
                name
                startsAt
                endsAt
                productCount
                mandatoryProductCount
                alreadyAdded
            }
            totalItems
        }
    }
`;

export const getShoppingListPromotionImportPreviewQuery = gql`
    query getShoppingListPromotionImportPreview(
        $shoppingListId: ID!
        $promotionIds: [ID!]!
    ) {
        getShoppingListPromotionImportPreview(
            input: {
                shoppingListId: $shoppingListId
                promotionIds: $promotionIds
            }
        ) {
            success
            message
            errorCode
            productCount
            newProductCount
            existingProductCount
            mandatoryProductCount
            promotionCountAfterImport
            alreadyAddedPromotionIds
        }
    }
`;

export const downloadTemplateUrlQuery = gql`
    query downloadTemplateUrl {
        downloadTemplateUrl
    }
`;

export const downloadProductsTemplateUrlQuery = gql`
    query downloadProductsTemplateUrl {
        downloadProductsTemplateUrl
    }
`;

export const getChannelsQuery = gql`
    query getShoppingListChannels($skip: Int, $take: Int) {
        channels(options: { skip: $skip, take: $take }) {
            items {
                id
                code
                customFields {
                    channelName
                    channelCode
                }
            }
            totalItems
        }
    }
`;

export const getShoppingListCommunicationBatchesQuery = gql`
    query getShoppingListCommunicationBatches(
        $shoppingListId: ID!
        $skip: Int
        $take: Int
    ) {
        getShoppingListCommunicationBatches(
            shoppingListId: $shoppingListId
            options: { skip: $skip, take: $take }
        ) {
            items {
                id
                createdAt
                updatedAt
                channel
                target
                selectionMode
                externalMessagingChannelId
                externalMessagingOperationCode
                templateName
                status
                totalRecipients
                sentCount
                failedCount
                skippedCount
                startedAt
                completedAt
                requestedBy {
                    id
                    firstName
                    lastName
                    emailAddress
                }
            }
            totalItems
        }
    }
`;

export const getShoppingListCommunicationRecipientsQuery = gql`
    query getShoppingListCommunicationRecipients(
        $batchId: ID!
        $skip: Int
        $take: Int
        $document: String
    ) {
        getShoppingListCommunicationRecipients(
            batchId: $batchId
            options: { skip: $skip, take: $take }
            document: $document
        ) {
            items {
                id
                createdAt
                updatedAt
                status
                emailAddress
                phoneNumber
                externalCorrelationId
                externalStatus
                externalErrorMessage
                errorMessage
                attempts
                sentAt
                externalAcceptedAt
                lastWebhookAt
                customer {
                    id
                    title
                    firstName
                    lastName
                    emailAddress
                    customFields {
                        document
                    }
                }
                promotion {
                    id
                    name
                }
            }
            totalItems
        }
    }
`;

export const createShoppingListMutation = gql`
    mutation createShoppingList($input: CreateShoppingListInput!) {
        createShoppingList(input: $input) {
            __typename
            ... on CreateShoppingListSuccess {
                shoppingListId
                success
            }
            ... on CreateShoppingListError {
                errorCode
                message
            }
        }
    }
`;

export const updateShoppingListMutation = gql`
    mutation updateShoppingList($input: UpdateShoppingListInput!) {
        updateShoppingList(input: $input) {
            __typename
            ... on UpdateShoppingListSuccess {
                success
            }
            ... on UpdateShoppingListError {
                errorCode
                message
            }
        }
    }
`;

export const deleteShoppingListMutation = gql`
    mutation removeShoppingLists($ids: [ID!]!) {
        removeShoppingLists(input: { ids: $ids }) {
            __typename
        }
    }
`;

export const importCustomersToShoppingListMutation = gql`
    mutation ImportCustomersToShoppingList(
        $shoppingListId: ID!
        $file: Upload!
    ) {
        importCustomersToShoppingList(
            shoppingListId: $shoppingListId
            file: $file
        ) {
            success
            message
            errorCode
            invalidCustomers
        }
    }
`;

export const importProductsToShoppingListMutation = gql`
    mutation ImportProductsToShoppingList(
        $shoppingListId: ID!
        $file: Upload!
    ) {
        importProductsToShoppingList(
            shoppingListId: $shoppingListId
            file: $file
        ) {
            success
            message
            errorCode
            importedCount
            notFoundProducts
            outOfStockProducts
            invalidRows {
                row
                ean
                quantity
                reason
            }
        }
    }
`;

export const importPromotionsToShoppingListMutation = gql`
    mutation ImportPromotionsToShoppingList(
        $shoppingListId: ID!
        $promotionIds: [ID!]!
    ) {
        importPromotionsToShoppingList(
            input: {
                shoppingListId: $shoppingListId
                promotionIds: $promotionIds
            }
        ) {
            success
            message
            errorCode
            importedCount
            linkedCount
            mandatoryCount
            importedPromotionIds
        }
    }
`;

export const searchShoppingListImportBrandsQuery = gql`
    query SearchShoppingListImportBrands(
        $input: SearchShoppingListImportBrandsInput!
    ) {
        searchShoppingListImportBrands(input: $input) {
            items {
                id
                code
                name
            }
            totalItems
        }
    }
`;

export const getShoppingListBrandImportProductsQuery = gql`
    query GetShoppingListBrandImportProducts(
        $input: GetShoppingListBrandImportProductsInput!
    ) {
        getShoppingListBrandImportProducts(input: $input) {
            items {
                productVariantId
                productId
                productName
                variantName
                sku
                codeEan
                measureCode
                measureAbbr
                measureName
                packageQuantity
                initialQuantity
                realStockLevel
                alreadyInList
            }
            totalItems
        }
    }
`;

export const importProductsByBrandToShoppingListMutation = gql`
    mutation ImportProductsByBrandToShoppingList(
        $input: ImportProductsByBrandToShoppingListInput!
    ) {
        importProductsByBrandToShoppingList(input: $input) {
            success
            message
            errorCode
            requestedCount
            importedCount
            skippedExistingCount
            skippedUnavailableCount
        }
    }
`;

export const removeCustomersFromShoppingListMutation = gql`
    mutation removeCustomersFromShoppingList($shoppingListId: ID!) {
        removeCustomersFromShoppingList(shoppingListId: $shoppingListId) {
            success
            message
            errorCode
        }
    }
`;

export const removeBuyersFromShoppingListMutation = gql`
    mutation removeBuyersFromShoppingList(
        $shoppingListId: ID!
        $customerIds: [ID!]!
    ) {
        removeBuyersFromShoppingList(
            input: {
                shoppingListId: $shoppingListId
                customerIds: $customerIds
            }
        ) {
            success
            message
            errorCode
        }
    }
`;

export const generateShoppingListCustomerAccessUrlMutation = gql`
    mutation generateShoppingListCustomerAccessUrl(
        $shoppingListId: ID!
        $customerId: ID!
    ) {
        generateShoppingListCustomerAccessUrl(
            input: { shoppingListId: $shoppingListId, customerId: $customerId }
        ) {
            success
            message
            errorCode
            url
            token
            expiresAt
            shoppingListId
            customerId
            sellerAdministratorId
        }
    }
`;

export const sendShoppingListCommunicationMutation = gql`
    mutation sendShoppingListCommunication(
        $shoppingListId: ID!
        $customerIds: [ID!]
        $channel: ShoppingListCommunicationChannel!
        $selectionMode: ShoppingListCommunicationSelectionMode
    ) {
        sendShoppingListCommunication(
            input: {
                shoppingListId: $shoppingListId
                customerIds: $customerIds
                channel: $channel
                selectionMode: $selectionMode
            }
        ) {
            success
            message
            errorCode
            batchId
            totalRecipients
            channel
        }
    }
`;

export const previewShoppingListPromotionCommunicationMutation = gql`
    mutation previewShoppingListPromotionCommunication(
        $shoppingListId: ID!
        $channel: ShoppingListCommunicationChannel!
        $assignments: [ShoppingListPromotionAssignmentInput!]!
    ) {
        previewShoppingListPromotionCommunication(
            input: {
                shoppingListId: $shoppingListId
                channel: $channel
                assignments: $assignments
            }
        ) {
            success
            message
            errorCode
            totalCustomers
            customersWithPromotion
            customersWithoutPromotion
            readyCount
            ineligibleCount
            invalidContactCount
            items {
                customerId
                promotionId
                customerName
                promotionName
                emailAddress
                phoneNumber
                status
                message
            }
        }
    }
`;

export const sendShoppingListPromotionCommunicationMutation = gql`
    mutation sendShoppingListPromotionCommunication(
        $shoppingListId: ID!
        $channel: ShoppingListCommunicationChannel!
        $assignments: [ShoppingListPromotionAssignmentInput!]!
    ) {
        sendShoppingListPromotionCommunication(
            input: {
                shoppingListId: $shoppingListId
                channel: $channel
                assignments: $assignments
            }
        ) {
            success
            message
            errorCode
            batchId
            totalRecipients
            channel
            target
        }
    }
`;

export const shareShoppingListPromotionByWhatsAppWebMutation = gql`
    mutation shareShoppingListPromotionByWhatsAppWeb(
        $shoppingListId: ID!
        $customerId: ID!
        $promotionId: ID!
    ) {
        shareShoppingListPromotionByWhatsAppWeb(
            input: {
                shoppingListId: $shoppingListId
                customerId: $customerId
                promotionId: $promotionId
            }
        ) {
            success
            message
            errorCode
            url
            whatsappWebUrl
            batchId
            recipientId
        }
    }
`;

export const removeItemsFromShoppingListMutation = gql`
    mutation removeItemsFromShoppingList(
        $shoppingListId: ID!
        $itemIds: [ID!]!
    ) {
        removeItemsFromShoppingList(
            input: { shoppingListId: $shoppingListId, itemIds: $itemIds }
        ) {
            success
            message
            errorCode
        }
    }
`;

export const removePromotionFromShoppingListMutation = gql`
    mutation removePromotionFromShoppingList(
        $shoppingListId: ID!
        $promotionId: ID!
    ) {
        removePromotionFromShoppingList(
            input: {
                shoppingListId: $shoppingListId
                promotionId: $promotionId
            }
        ) {
            success
            message
            errorCode
        }
    }
`;

export const assignShoppingListToChannelsMutation = gql`
    mutation assignShoppingListToChannels(
        $shoppingListIds: [ID!]!
        $channelIds: [ID!]!
    ) {
        assignShoppingListToChannels(
            input: {
                shoppingListIds: $shoppingListIds
                channelIds: $channelIds
            }
        ) {
            id
            channels {
                id
                code
                customFields {
                    channelName
                    channelCode
                }
            }
        }
    }
`;

export const removeChannelFromShoppingListMutation = gql`
    mutation removeChannelFromShoppingList(
        $shoppingListIds: [ID!]!
        $channelIds: [ID!]!
    ) {
        removeChannelFromShoppingList(
            input: {
                shoppingListIds: $shoppingListIds
                channelIds: $channelIds
            }
        ) {
            id
            channels {
                id
                code
                customFields {
                    channelName
                    channelCode
                }
            }
        }
    }
`;
